/**
 * Gestion d'état du back-office.
 *
 * Zustand, et non un conteneur global : un magasin est une fonction, il se
 * crée par instance, et une suite de tests n'hérite donc pas de l'état du test
 * précédent. C'est la seule propriété qui compte pour un squelette — le reste
 * viendra avec les écrans.
 *
 * **Aucun état métier ici.** Ce magasin porte ce qui appartient à la session de
 * travail : la langue choisie, le dernier module consulté, et la session
 * d'opérateur ouverte par `B-01`. Les epics 1 à 14 poseront leurs propres
 * magasins.
 *
 * **Le jeton ne survit pas à la fermeture de l'onglet ; il survit à un
 * rechargement.** Ce sont deux événements différents, et l'en-tête précédent
 * les confondait : il interdisait tout stockage au motif qu'un jeton de
 * back-office « n'a aucune raison de survivre à la fermeture de l'onglet » —
 * une raison juste, une règle trop large. Un `F5` détruit le tas JavaScript,
 * donc le magasin, donc la session : le porteur repassait par le mot de passe
 * **et** le code reçu par SMS à chaque rafraîchissement de page.
 *
 * La session est donc écrite dans `sessionStorage`, dont la portée est
 * exactement celle que la règle demandait : elle survit à un rechargement et
 * **meurt avec l'onglet**. `localStorage` reste interdit — il donnerait la
 * vacation d'un agent à qui rouvre le navigateur du poste le lendemain —, et
 * une suite le vérifie sur le stockage réel plutôt que sur l'intention.
 *
 * **Ce que cela coûte, sans l'enjoliver.** `sessionStorage` est plus faible que
 * la mémoire face à une injection de script ; de peu, parce que du script
 * injecté dans la page lit un magasin Zustand aussi bien qu'il lit
 * `sessionStorage`. La protection réellement plus forte — un cookie
 * `httpOnly; Secure; SameSite`, qu'aucun script ne lit — **n'est pas ce qui est
 * fait ici** : elle demande une anti-CSRF et une modification du serveur. Le
 * raisonnement complet, et ce que la restauration vérifie avant de croire ce
 * qu'elle lit, sont dans `session-persistence.ts`.
 */

import { createStore } from 'zustand/vanilla';

import {
  browserSessionStorage,
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from './session-persistence.js';
import type { SessionStorageLike } from './session-persistence.js';
import { defaultLocale } from '../i18n/index.js';

/**
 * La session d'un opérateur, telle que `POST /api/backoffice/session` la rend.
 *
 * Les quatre champs sont ceux de `BackOfficeMessages.LoginResponse` : le
 * contrat est déjà livré, et cette déclaration le suit sans rien y ajouter.
 */
export interface OperatorSession {
  /** Le jeton porteur. **Mémoire et onglet** — voir l'en-tête de ce fichier. */
  readonly token: string;
  /**
   * Le jeton d'échange, remis par la même réponse (dette `D-30`).
   *
   * Il vit huit heures là où le jeton d'accès en vit une, et c'est ce qui fixe
   * la portée du stockage : `sessionStorage`, qui meurt avec l'onglet, jamais
   * `localStorage`, qui donnerait la vacation entière d'un agent à qui rouvre
   * le navigateur du poste.
   */
  readonly refreshToken: string;
  /** L'instant d'expiration du jeton d'accès, tel que le serveur l'écrit. */
  readonly expiresAt: string;
  /** L'instant d'expiration du jeton d'échange. */
  readonly refreshExpiresAt: string;
  /** Le code du rôle habilité. */
  readonly role: string;
  /** Le nom affiché de l'opérateur. */
  readonly displayName: string;
  /**
   * L'identifiant **opaque** de l'opérateur, et son seul usage : se désigner à la
   * mesure d'audience (PostHog). Absent d'un serveur antérieur au champ.
   *
   * **Il n'est pas recopié dans le stockage de l'onglet** (`persistedSessionFields`
   * ne le nomme pas) : `posthog-js` garde lui-même l'identité de l'onglet, et une
   * session restaurée sans lui reste correctement attribuée.
   */
  readonly operatorId?: string;
}

/**
 * Ce que la matrice dit des modules, tel que le magasin le retient.
 *
 * La forme suit `back-office-modules.ts`, qui suit `BackOfficeMessages` : le contrat
 * est déjà livré, et cette déclaration ne lui ajoute rien.
 */
export interface OperatorModulesState {
  readonly role: string;
  readonly decidableModules: readonly string[];
  readonly readableModules: readonly string[];
}

/**
 * Pourquoi la session de travail s'est arrêtée.
 *
 * Il ne suffit pas de savoir qu'il n'y a plus de session : le retour à `B-01`
 * doit **dire** ce qui s'est passé. Une session tombée par inactivité pendant
 * une saisie ne se distingue autrement pas d'un premier lancement, et
 * l'opérateur ne saurait pas que ce qu'il tapait n'a pas été enregistré.
 *
 * **Un seul motif, et volontairement.** Une déconnexion volontaire n'en produit
 * pas : l'opérateur qui vient de cliquer sur « se déconnecter » sait pourquoi
 * il est revenu à `B-01`, et lui afficher un avertissement apprendrait à
 * ignorer celui qui compte. `logout` remet donc le motif à `null`.
 */
export type SessionEndReason = 'expired';

/** L'état de session. */
export interface SessionState {
  /** La langue choisie par l'opérateur. */
  readonly locale: string;
  /** Le dernier module consulté, pour y revenir à la réouverture. */
  readonly lastModuleId: string | null;
  /** La session ouverte, ou `null` tant que `B-01` n'a pas abouti. */
  readonly operator: OperatorSession | null;
  /**
   * Ce que la matrice dit des modules, ou `null` tant qu'on ne l'a pas lu.
   *
   * **Non persisté, et délibérément.** La matrice « doit être configurable »
   * (`EX-BO-002`) : la relire à chaque ouverture d'onglet coûte un appel et rend
   * l'effet d'un changement d'habilitation immédiat, là où une copie stockée le
   * ferait traîner jusqu'à la fin de la vacation. Et surtout : ceci n'accorde
   * rien — le serveur refuse par ses propres contrôles —, donc une copie périmée
   * ne peut pas ouvrir une porte, seulement en proposer une qui restera fermée.
   */
  readonly modules: OperatorModulesState | null;
  /** Pourquoi la session précédente s'est arrêtée, ou `null`. */
  readonly endReason: SessionEndReason | null;
  /** Change la langue, sans rechargement de l'application. */
  setLocale(locale: string): void;
  /** Enregistre le module consulté. */
  visitModule(moduleId: string): void;
  /** Ouvre la session rendue par le serveur. */
  openSession(session: OperatorSession): void;
  /**
   * Remplace le couple de jetons par celui que la rotation vient de rendre.
   *
   * Distinct d'[openSession], et pas par goût : une rotation ne recommence pas la
   * session de travail. Le module consulté et la langue survivent, et — le jour où
   * l'écran affichera « votre session a été renouvelée » — c'est cette entrée-là
   * qu'il écoutera, pas celle d'une connexion.
   */
  renewSession(session: OperatorSession): void;
  /** Enregistre ce que la matrice a répondu pour l'opérateur connecté. */
  setModules(modules: OperatorModulesState): void;
  /** Ferme la session parce qu'elle a expiré par inactivité. */
  expireSession(): void;
  /**
   * Ferme la session parce que l'opérateur s'en va.
   *
   * **Le poste est partagé.** Les agents du back-office se relaient sur les
   * mêmes terminaux ; depuis que la session est écrite dans l'onglet, une
   * déconnexion qui ne ferait qu'oublier l'objet en mémoire laisserait le
   * couple de jetons lisible par le suivant — un défaut pire que le
   * rechargement qu'on vient de corriger. C'est la raison d'être de cette
   * entrée, et c'est ce que la suite éprouve sur le stockage réel.
   */
  logout(): void;
}

/** Le type du magasin, exposé pour que les tests le construisent eux-mêmes. */
export type SessionStore = ReturnType<typeof createSessionStore>;

/**
 * Construit un magasin de session.
 *
 * [onLocaleChange] est appelé à chaque bascule : c'est par là que l'instance
 * i18next apprend la nouvelle langue. La dépendance est **injectée** plutôt
 * qu'importée, sinon le magasin ne serait testable qu'avec l'i18n de
 * l'application entière.
 *
 * **[storage] par défaut, c'est le `sessionStorage` du navigateur** : la
 * persistance est le comportement de production, pas une option qu'un point
 * d'entrée pourrait oublier d'activer. Un test qui veut un stockage à lui — ou
 * pas de stockage du tout — le passe explicitement. [initialSession], lui,
 * l'emporte sur ce qui est stocké, y compris quand il vaut `null` : c'est la
 * couture par laquelle une suite décrit l'état de départ qu'elle annonce.
 */
export function createSessionStore(options: {
  initialLocale?: string;
  initialSession?: OperatorSession | null;
  onLocaleChange?: (locale: string) => void;
  storage?: SessionStorageLike | null;
  now?: () => number;
} = {}) {
  const onLocaleChange = options.onLocaleChange;
  const stockage = options.storage === undefined ? browserSessionStorage() : options.storage;
  const maintenant = options.now ?? (() => Date.now());
  // Ce qui sort du stockage est vérifié avant d'être cru — échéance d'échange
  // dépassée, charge illisible, champ manquant : voir `session-persistence.ts`.
  const auDemarrage =
    options.initialSession === undefined
      ? readStoredSession(stockage, maintenant)
      : options.initialSession;

  return createStore<SessionState>((set) => ({
    locale: options.initialLocale ?? defaultLocale,
    lastModuleId: null,
    operator: auDemarrage,
    modules: null,
    endReason: null,
    setLocale(locale: string): void {
      set({ locale });
      onLocaleChange?.(locale);
    },
    visitModule(moduleId: string): void {
      set({ lastModuleId: moduleId });
    },
    openSession(session: OperatorSession): void {
      // Le motif d'arrêt précédent est effacé en même temps : garder
      // « expirée » après une reconnexion réussie ferait afficher l'avertissement
      // à la session suivante, et un avertissement qui ne correspond à rien
      // s'apprend à être ignoré.
      // Les modules de l'opérateur PRÉCÉDENT sont oubliés ici : sur un poste
      // partagé, les garder ferait afficher au suivant le menu de son collègue.
      set({ operator: session, modules: null, endReason: null });
      writeStoredSession(stockage, session);
    },
    renewSession(session: OperatorSession): void {
      set({ operator: session });
      // La rotation est écrite, elle aussi. Sans cela, un rechargement après un
      // renouvellement restaurerait l'ANCIEN jeton d'échange — que le serveur
      // traite comme un rejeu : il fait tomber toutes les sessions de l'agent
      // et crie au vol sur son propre travail.
      writeStoredSession(stockage, session);
    },
    setModules(modules: OperatorModulesState): void {
      set({ modules });
    },
    expireSession(): void {
      set({ operator: null, modules: null, endReason: 'expired' });
      clearStoredSession(stockage);
    },
    logout(): void {
      set({ operator: null, modules: null, endReason: null });
      clearStoredSession(stockage);
    },
  }));
}
